import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAffiliatePreview } from '@/hooks/useAffiliatePreview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { GradientAvatar } from '@/components/dashboard-ui/GradientAvatar'
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Copy,
  Flag,
  Forward,
  Inbox,
  Maximize2,
  Minimize2,
  Mail,
  MailOpen,
  MailPlus,
  MoveLeft,
  MoveRight,
  Paperclip,
  PenLine,
  Plus,
  RefreshCw,
  Reply,
  ReplyAll,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  UserRound,
  X,
} from 'lucide-react'

interface EmailAlias {
  id: string
  local_part: string
  email_address: string
  display_name: string
  status: string
  signature_text: string
  signature_enabled: boolean
}

interface EmailThread {
  id: string
  subject: string
  participants: string[]
  last_message_at: string
  last_message_preview: string | null
  unread_count: number
  status: string
  alias_email: string
  last_direction?: 'inbound' | 'outbound'
  last_status?: string
  flagged?: boolean
  deleted_at?: string | null
  folder_id?: string | null
  muted?: boolean
}

interface EmailMessage {
  id: string
  direction: 'inbound' | 'outbound'
  from_email: string
  from_name: string | null
  to_addresses: string[]
  cc_addresses: string[]
  subject: string
  text_body: string | null
  html_body: string | null
  snippet: string | null
  status: string
  error_message: string | null
  attachments: Array<{ id?: string; filename?: string; content_type?: string; size?: number }>
  sent_at: string | null
  received_at: string | null
  created_at: string
}

type Folder = 'inbox' | 'sent' | 'flagged' | 'all' | 'trash' | `folder:${string}`

interface EmailFolder {
  id: string
  name: string
  sort_order: number
  thread_count?: number
  unread_count?: number
}

interface ThreadMenuState {
  threadId: string
  x: number
  y: number
}

interface FolderMenuState {
  folderId: string
  x: number
  y: number
}

// Attachment picker policy mirrors the server: 30MB total, 10 files max, and
// a small extension blocklist. We validate on the client so the user gets a
// fast "this file is too big" toast instead of a 45MB upload round-trip
// failure, but the backend still re-checks everything.
const ATTACHMENT_MAX_BYTES = 30 * 1024 * 1024
const ATTACHMENT_MAX_COUNT = 10
const ATTACHMENT_BLOCKED_EXTENSIONS = new Set([
  'exe', 'bat', 'scr', 'js', 'vbs', 'com', 'pif', 'cmd', 'msi', 'jar', 'app', 'ps1', 'sh',
])

interface DraftAttachment {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  contentBase64: string
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function extensionOfFile(name: string): string {
  const match = name.match(/\.([a-z0-9]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

// FileReader → base64 (strip the "data:...;base64," prefix so the server gets
// the raw payload). Promise-wrapped because the picker is async-friendly.
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const idx = result.indexOf(',')
      resolve(idx >= 0 ? result.slice(idx + 1) : result)
    }
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// Pretty timestamps: relative for the last ~24h (the "is this fresh?" glance),
// then a clean absolute label for everything older so the eye doesn't have
// to do math while scanning the thread list.
function formatThreadDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  const diff = Date.now() - date.getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hours = Math.floor(min / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatFullDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function messageText(message: EmailMessage): string {
  if (message.text_body?.trim()) return message.text_body
  return (message.html_body || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Tag + attribute allowlist used by sanitizeInboundHtml. Anything outside
// these lists is dropped to keep cross-site scripts, tracking pixels with
// JS handlers, and weird CSS payloads from making it onto the page.
const ALLOWED_HTML_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'code', 'del', 'div', 'em', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'mark', 'ol', 'p', 'pre', 's',
  'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot',
  'th', 'thead', 'tr', 'u', 'ul',
])
const ALLOWED_HTML_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'width', 'height', 'title']),
  // Table layout is the entire backbone of real-world email signatures —
  // align/valign/width/cellpadding/cellspacing/colspan/rowspan carry the
  // grid structure that "looks broken" without.
  table: new Set(['align', 'border', 'cellpadding', 'cellspacing', 'width']),
  td: new Set(['align', 'valign', 'colspan', 'rowspan', 'width', 'height', 'bgcolor']),
  th: new Set(['align', 'valign', 'colspan', 'rowspan', 'width', 'height']),
  tr: new Set(['align', 'valign']),
  // Universal: allow style + a couple of layout hints. We're rendering
  // inside a sandboxed iframe (no scripts), so CSS payloads can't carry
  // active content; tracking via background-image URL is the only concern
  // and is harmless beyond a beacon load.
  '*': new Set(['style', 'align', 'valign', 'width', 'height']),
}

// Reject style strings that try to do anything funky. Belt-and-suspenders;
// the iframe sandbox already blocks scripts, but a clean style declaration
// keeps the DOM tidy and stops `url(javascript:...)` quirks in old browsers.
function isSafeStyleValue(value: string): boolean {
  const lower = value.toLowerCase()
  if (/url\s*\(\s*['"]?\s*javascript:/i.test(lower)) return false
  if (/expression\s*\(/i.test(lower)) return false
  if (/behaviou?r\s*:/i.test(lower)) return false
  return true
}

// Browser-native sanitizer. Walks a parsed DOM tree, allowing only the tags
// and attributes above, normalising links to open in a new tab with safe
// rel, and rejecting anything with a javascript: / data: scheme except for
// data:image/* on <img>. Returning serialised HTML rather than DOM nodes
// so the caller can use dangerouslySetInnerHTML without holding the parsed
// document around.
function sanitizeInboundHtml(raw: string): string {
  if (typeof window === 'undefined' || !raw) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return ''

  function walk(node: Element): void {
    // Bottom-up traversal so removed children don't mess up indices.
    const children = Array.from(node.children)
    for (const child of children) walk(child)

    const tag = node.tagName.toLowerCase()
    if (!ALLOWED_HTML_TAGS.has(tag)) {
      // Some disallowed tags (style/script/head/meta/link) carry text content
      // that should NEVER reach the page — e.g. Proofpoint wraps anti-phish
      // CSS in `<style><!-- ... --></style>`. Unwrapping its children would
      // dump that CSS as visible text. For these tags, remove the whole
      // subtree. For other unknown tags (article/section/etc.) preserve
      // the inner content by unwrapping.
      const dropEntirely = new Set(['style', 'script', 'head', 'meta', 'link', 'title', 'base', 'noscript'])
      if (dropEntirely.has(tag)) {
        node.parentNode?.removeChild(node)
        return
      }
      while (node.firstChild) node.parentNode?.insertBefore(node.firstChild, node)
      node.parentNode?.removeChild(node)
      return
    }

    const allowedAttrs = new Set([
      ...(ALLOWED_HTML_ATTRS['*'] || []),
      ...(ALLOWED_HTML_ATTRS[tag] || []),
    ])
    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase()
      if (!allowedAttrs.has(name)) {
        node.removeAttribute(attr.name)
        continue
      }
      // URL safety: hrefs and srcs must be http/https/mailto/tel; img also
      // allows data:image/* for inline base64 logos.
      if (name === 'href' || name === 'src') {
        const v = attr.value.trim()
        const ok = /^(https?:|mailto:|tel:|#)/i.test(v)
          || (tag === 'img' && /^data:image\//i.test(v))
        if (!ok) {
          node.removeAttribute(attr.name)
          continue
        }
      }
      // style is allowed but only if it doesn't try to run JS or call out
      // to expression()/behavior:. Beyond that we trust the iframe sandbox
      // to keep things contained.
      if (name === 'style' && !isSafeStyleValue(attr.value)) {
        node.removeAttribute(attr.name)
        continue
      }
    }
    if (tag === 'a') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }
  walk(root)
  return root.innerHTML
}

// Split a message body into the "fresh" portion (what the sender actually
// wrote this round) and the "quoted" portion (the previous thread cruft
// they're replying to). Tries patterns in order:
//   1) Gmail-style "On <date>, <name> wrote:" boundary line
//   2) Outlook-style "From: ... Sent: ... To: ... Subject:" header block
//   3) First contiguous run of lines starting with ">"
// Anything before the boundary is fresh; everything from the boundary down
// is quoted. If no pattern matches, fresh = whole body and quoted = null.
function splitQuotedContent(text: string): { fresh: string; quoted: string | null } {
  const body = String(text || '')
  if (!body.trim()) return { fresh: '', quoted: null }

  // Pattern 1: Gmail/Apple Mail "On <date>, <name> wrote:" intro line.
  // Match up to ~300 chars because some clients wrap the line and we want
  // the whole intro caught as the boundary.
  const gmailIntro = body.match(/\n\s*On [\s\S]{1,300}?wrote:\s*\n/i)
  if (gmailIntro && typeof gmailIntro.index === 'number') {
    const fresh = body.slice(0, gmailIntro.index).trim()
    const quoted = body.slice(gmailIntro.index).trim()
    if (fresh && quoted) return { fresh, quoted }
  }

  // Pattern 2: Outlook "From: ... \n Sent: ... \n To: ..." header block.
  // Anchored on "From:" at line start followed within a few lines by Sent:
  // and To:, then Subject:. Match the first occurrence; what's above it is
  // the new content, everything from there down is the quoted reply.
  const outlookIntro = body.match(/(?:^|\n)\s*[_-]*\s*\n*\s*From: [\s\S]{1,400}?Subject:[^\n]+\n/i)
  if (outlookIntro && typeof outlookIntro.index === 'number') {
    const fresh = body.slice(0, outlookIntro.index).trim()
    const quoted = body.slice(outlookIntro.index).trim()
    if (fresh && quoted) return { fresh, quoted }
  }

  // Pattern 3: first run of lines starting with ">"
  const lines = body.split('\n')
  let firstQuoteIdx = -1
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*>/.test(lines[i])) {
      firstQuoteIdx = i
      break
    }
  }
  if (firstQuoteIdx > 0) {
    const fresh = lines.slice(0, firstQuoteIdx).join('\n').trim()
    const quoted = lines.slice(firstQuoteIdx).join('\n').trim()
    if (fresh && quoted) return { fresh, quoted }
  }

  return { fresh: body, quoted: null }
}

function statusVariant(status?: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (!status) return 'outline'
  if (['failed', 'bounced', 'suppressed', 'complained'].includes(status)) return 'destructive'
  if (['delivered', 'opened', 'clicked'].includes(status)) return 'secondary'
  return 'outline'
}

function signatureLines(value: string): string[] {
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

// Pull a display name out of "Sarah Chen <sarah@firm.law>" or just the email
// local-part as a fallback. Used only for the avatar initials + list display
// so the gradient stays stable across messages from the same sender.
function senderDisplayName(name: string | null, email: string): string {
  if (name && name.trim()) return name.trim()
  const local = email.split('@')[0] || email
  return local
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

// First non-alias participant — used as the visual "other person" on a thread.
// Falls back to the first participant if none exist outside the alias domain.
function otherParticipant(thread: EmailThread, aliasEmail?: string): string {
  const others = (thread.participants || []).filter(
    p => !aliasEmail || p.toLowerCase() !== aliasEmail.toLowerCase()
  )
  return others[0] || thread.participants?.[0] || ''
}

export default function AffiliateEmailPanel() {
  const { toast } = useToast()
  const { isPreview, apiParams } = useAffiliatePreview()
  const [aliases, setAliases] = useState<EmailAlias[]>([])
  const [threads, setThreads] = useState<EmailThread[]>([])
  const [messages, setMessages] = useState<EmailMessage[]>([])
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null)
  const [folder, setFolder] = useState<Folder>('inbox')
  const [loading, setLoading] = useState(true)
  const [threadLoading, setThreadLoading] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  // When compose is open, popout=false means it's embedded in the right pane;
  // popout=true means it's rendered in a full dialog instead. The toolbar
  // Compose button defaults to embedded so the user gets the entire right
  // pane to type in; the "Pop out" button switches to dialog mode if they
  // want a focused-modal feel.
  const [composePopout, setComposePopout] = useState(false)
  const [displayNameOpen, setDisplayNameOpen] = useState(false)
  const [displayNameSaving, setDisplayNameSaving] = useState(false)
  const [displayNameDraft, setDisplayNameDraft] = useState('')
  const [signatureOpen, setSignatureOpen] = useState(false)
  const [signatureSaving, setSignatureSaving] = useState(false)
  const [signatureDraft, setSignatureDraft] = useState({ text: '', enabled: true })
  const [sending, setSending] = useState(false)
  const [compose, setCompose] = useState({ to: '', cc: '', bcc: '', subject: '', textBody: '' })
  const [composeShowCc, setComposeShowCc] = useState(false)
  const [composeShowBcc, setComposeShowBcc] = useState(false)
  const [composeAttachments, setComposeAttachments] = useState<DraftAttachment[]>([])
  const [replyAttachments, setReplyAttachments] = useState<DraftAttachment[]>([])
  const [replyBody, setReplyBody] = useState('')
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null)
  const [pendingReplyFocus, setPendingReplyFocus] = useState(false)
  const [aliasCopied, setAliasCopied] = useState(false)
  const [threadMenu, setThreadMenu] = useState<ThreadMenuState | null>(null)
  const [folderMenu, setFolderMenu] = useState<FolderMenuState | null>(null)
  const [actionBusy, setActionBusy] = useState<string | null>(null)
  // AI polish state. composeUndoText / replyUndoText hold the pre-improve
  // draft so a partner can roll back if the AI rewrote something they liked.
  const [composeImproving, setComposeImproving] = useState(false)
  const [replyImproving, setReplyImproving] = useState(false)
  const [composeUndoText, setComposeUndoText] = useState<string | null>(null)
  const [replyUndoText, setReplyUndoText] = useState<string | null>(null)
  const [folders, setFolders] = useState<EmailFolder[]>([])
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [folderDraft, setFolderDraft] = useState('')
  const [folderSaving, setFolderSaving] = useState(false)
  const [renameTarget, setRenameTarget] = useState<EmailFolder | null>(null)

  const primaryAlias = aliases.find(alias => alias.status === 'active') || aliases[0]
  const signaturePreviewLines = signatureLines(signatureDraft.text)
  const senderNameLabel = primaryAlias?.display_name?.trim() || 'Legal Lady'
  const senderIdentity = primaryAlias
    ? `${senderNameLabel} <${primaryAlias.email_address}>`
    : 'your affiliate alias'
  const totalUnread = useMemo(
    () => threads.reduce((sum, thread) => sum + (thread.unread_count || 0), 0),
    [threads]
  )

  // Broadcast unread count so the sidebar badge updates instantly while the
  // user is in the panel — opening a thread clears its unread_count, which
  // re-fires this effect with the lower total. Skip while previewing so an
  // admin browsing someone else's inbox doesn't replace the admin's own
  // badge (preview view doesn't own a badge anyway).
  useEffect(() => {
    if (isPreview) return
    window.dispatchEvent(
      new CustomEvent('affiliate-email-unread', { detail: { count: totalUnread } })
    )
  }, [totalUnread, isPreview])

  useEffect(() => {
    if (!primaryAlias || displayNameOpen) return
    setDisplayNameDraft(primaryAlias.display_name || '')
  }, [displayNameOpen, primaryAlias?.display_name, primaryAlias?.id])

  useEffect(() => {
    if (!primaryAlias || signatureOpen) return
    setSignatureDraft({
      text: primaryAlias.signature_text || '',
      enabled: primaryAlias.signature_enabled !== false,
    })
  }, [primaryAlias?.id, primaryAlias?.signature_enabled, primaryAlias?.signature_text, signatureOpen])

  const loadAliases = useCallback(async () => {
    const res = await api.get('/api/affiliates/me/email/aliases', { params: apiParams })
    setAliases(res.data.aliases || [])
  }, [apiParams])

  const loadFolders = useCallback(async () => {
    try {
      const res = await api.get('/api/affiliates/me/email/folders', { params: apiParams })
      setFolders(res.data.folders || [])
    } catch {
      // Folders feature is optional from the affiliate's POV — a fetch
      // failure (e.g. older API without the endpoint) shouldn't surface
      // as a destructive toast or block loading threads.
      setFolders([])
    }
  }, [apiParams])

  const loadThreads = useCallback(async (nextFolder = folder) => {
    const res = await api.get('/api/affiliates/me/email/threads', {
      params: { folder: nextFolder, ...(apiParams || {}) },
    })
    const nextThreads = res.data.threads || []
    setThreads(nextThreads)
    setSelectedThread(prev => {
      if (prev && !nextThreads.some((thread: EmailThread) => thread.id === prev.id)) {
        setMessages([])
        return null
      }
      return prev
    })
  }, [folder, apiParams])

  const loadThread = useCallback(async (thread: EmailThread) => {
    setSelectedThread(thread)
    setThreadLoading(true)
    setReplyAttachments([]) // fresh reply box per thread
    setReplyUndoText(null)


    try {
      const res = await api.get(`/api/affiliates/me/email/threads/${thread.id}`, { params: apiParams })
      setSelectedThread(res.data.thread)
      setMessages(res.data.messages || [])
      setThreads(prev => prev.map(item => item.id === thread.id ? { ...item, unread_count: 0 } : item))
    } catch (err: any) {
      toast({
        title: 'Failed to load email thread',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setThreadLoading(false)
    }
  }, [toast, apiParams])

  // Toolbar / right-click actions. We optimistically update the local thread
  // row so the UI reacts instantly (Gmail-style); the API call confirms or
  // rolls back. Closing the open thread on delete keeps the right pane from
  // showing a row that vanished from the list.
  const patchThread = useCallback(async (
    threadId: string,
    patch: { flagged?: boolean; markUnread?: boolean; markRead?: boolean; deleted?: boolean; folderId?: string | null; muted?: boolean },
    optimistic?: Partial<EmailThread>,
  ): Promise<void> => {
    setActionBusy(threadId)
    if (optimistic) {
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, ...optimistic } : t))
      setSelectedThread(prev => prev && prev.id === threadId ? { ...prev, ...optimistic } : prev)
    }
    try {
      await api.patch(`/api/affiliates/me/email/threads/${threadId}`, patch, { params: apiParams })
      // Anything that moves a thread between buckets (delete/restore/folder
      // move) should refresh the list — what's currently shown depends on the
      // active folder. Folder counts on the tab also need to recompute, so
      // pull folders again on those transitions.
      if (patch.deleted !== undefined || patch.folderId !== undefined) {
        if (patch.deleted !== undefined && selectedThread?.id === threadId) {
          setSelectedThread(null)
          setMessages([])
          setReplyAttachments([])
        }
        await loadThreads(folder)
        await loadFolders()
      }
    } catch (err: any) {
      toast({
        title: 'Action failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
      // Rollback: refetch the truth from the server rather than reconstruct
      // the previous local state, which would also need to know unread_count
      // before the optimistic flip.
      await loadThreads(folder)
    } finally {
      setActionBusy(null)
    }
  }, [apiParams, folder, loadFolders, loadThreads, selectedThread?.id, toast])

  // After Reply / Reply all is chosen from the context menu we want to land
  // on the thread with the textarea already focused. Threads load async, so
  // we set a flag and let an effect run once the messages are in.
  useEffect(() => {
    if (!pendingReplyFocus) return
    if (threadLoading || !selectedThread) return
    const node = replyTextareaRef.current
    if (node) {
      node.focus()
      node.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setPendingReplyFocus(false)
  }, [pendingReplyFocus, threadLoading, selectedThread])

  // Reply / Reply all (both behave the same today: the reply composer
  // already addresses every participant). Loading the thread first ensures
  // the textarea exists for us to focus.
  const replyToThread = useCallback(async (thread: EmailThread): Promise<void> => {
    setPendingReplyFocus(true)
    if (selectedThread?.id !== thread.id) {
      await loadThread(thread)
    }
  }, [loadThread, selectedThread?.id])

  // Forward: pull the thread's last message and pre-populate the compose
  // form with an Outlook-style forwarded block ("---------- Forwarded
  // message ----------" header). Attachments aren't auto-attached — the
  // user can download the originals from the thread view and re-attach if
  // they want them included.
  const forwardThread = useCallback(async (thread: EmailThread): Promise<void> => {
    try {
      const res = await api.get(`/api/affiliates/me/email/threads/${thread.id}`, { params: apiParams })
      const fullMessages: EmailMessage[] = res.data?.messages || []
      const last = fullMessages[fullMessages.length - 1]
      if (!last) {
        toast({ title: 'Nothing to forward', description: 'The thread has no messages yet.' })
        return
      }
      const senderName = last.from_name?.trim() || last.from_email || 'Unknown sender'
      const date = formatFullDate(last.sent_at || last.received_at || last.created_at)
      const subject = thread.subject || last.subject || ''
      const fwdSubject = /^fwd:/i.test(subject) ? subject : `Fwd: ${subject || '(no subject)'}`
      const recipients = (last.to_addresses || []).join(', ')
      const body = messageText(last)
      const forwardedBlock = [
        '',
        '',
        '---------- Forwarded message ----------',
        `From: ${senderName} <${last.from_email}>`,
        `Date: ${date}`,
        `Subject: ${subject || '(no subject)'}`,
        recipients ? `To: ${recipients}` : '',
        '',
        body,
      ].filter(line => line !== '').concat(['']).join('\n')

      setCompose({ to: '', cc: '', bcc: '', subject: fwdSubject, textBody: forwardedBlock })
      setComposeAttachments([])
      setComposeShowCc(false)
      setComposeShowBcc(false)
      setComposeUndoText(null)
      setComposeOpen(true)
      // Forward needs typing space; default to embedded so the user gets
      // the full pane. They can pop out if they prefer.
      setComposePopout(false)
      setSelectedThread(null) // free the right pane for compose
      setMessages([])
    } catch (err: any) {
      toast({
        title: 'Could not load thread to forward',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }, [apiParams, toast])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      await loadAliases()
      await loadFolders()
      await loadThreads(folder)
    } catch (err: any) {
      toast({
        title: 'Failed to load affiliate email',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [folder, loadAliases, loadFolders, loadThreads, toast])

  // Folder CRUD ---
  async function createFolder() {
    const trimmed = folderDraft.trim()
    if (!trimmed) return
    setFolderSaving(true)
    try {
      const res = await api.post('/api/affiliates/me/email/folders', { name: trimmed }, { params: apiParams })
      setFolders(prev => [...prev, res.data.folder].sort((a, b) => a.sort_order - b.sort_order))
      setNewFolderOpen(false)
      setFolderDraft('')
      toast({ title: `Folder "${res.data.folder.name}" created` })
    } catch (err: any) {
      toast({
        title: 'Could not create folder',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setFolderSaving(false)
    }
  }

  async function renameFolder() {
    if (!renameTarget) return
    const trimmed = folderDraft.trim()
    if (!trimmed || trimmed === renameTarget.name) {
      setRenameTarget(null)
      return
    }
    setFolderSaving(true)
    try {
      const res = await api.patch(
        `/api/affiliates/me/email/folders/${renameTarget.id}`,
        { name: trimmed },
        { params: apiParams },
      )
      setFolders(prev => prev.map(f => f.id === res.data.folder.id ? { ...f, ...res.data.folder } : f))
      setRenameTarget(null)
      setFolderDraft('')
      toast({ title: 'Folder renamed' })
    } catch (err: any) {
      toast({
        title: 'Could not rename folder',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setFolderSaving(false)
    }
  }

  async function deleteFolder(target: EmailFolder) {
    if (!window.confirm(`Delete folder "${target.name}"? Threads inside will move back to your inbox.`)) return
    try {
      await api.delete(`/api/affiliates/me/email/folders/${target.id}`, { params: apiParams })
      setFolders(prev => prev.filter(f => f.id !== target.id))
      // If the user was viewing this folder, kick them back to Inbox
      if (folder === `folder:${target.id}`) {
        setFolder('inbox')
      }
      toast({ title: `Folder "${target.name}" deleted` })
    } catch (err: any) {
      toast({
        title: 'Could not delete folder',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    void load()
  }, [load])

  // Deep-link from the notification bell: /affiliate/email?thread=<id>. After
  // threads load, find the thread and select it once. We use a ref so a later
  // refresh (which re-runs load + threads) doesn't keep yanking the user back
  // to that thread if they've navigated away.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkedThread = useRef<string | null>(null)
  useEffect(() => {
    const target = searchParams.get('thread')
    if (!target || deepLinkedThread.current === target) return
    if (threads.length === 0) return
    const match = threads.find(t => t.id === target)
    if (!match) return
    deepLinkedThread.current = target
    void loadThread(match)
    const next = new URLSearchParams(searchParams)
    next.delete('thread')
    setSearchParams(next, { replace: true })
  }, [threads, searchParams, setSearchParams, loadThread])

  const replyRecipients = useMemo(() => {
    if (!selectedThread || !primaryAlias) return []
    return (selectedThread.participants || []).filter(
      participant => participant.toLowerCase() !== primaryAlias.email_address.toLowerCase()
    )
  }, [primaryAlias, selectedThread])

  async function copyAlias() {
    if (!primaryAlias) return
    try {
      await navigator.clipboard.writeText(primaryAlias.email_address)
      setAliasCopied(true)
      toast({ title: 'Email alias copied' })
      window.setTimeout(() => setAliasCopied(false), 1500)
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  function openSignatureEditor() {
    if (!primaryAlias) return
    setSignatureDraft({
      text: primaryAlias.signature_text || '',
      enabled: primaryAlias.signature_enabled !== false,
    })
    setSignatureOpen(true)
  }

  function openDisplayNameEditor() {
    if (!primaryAlias) return
    setDisplayNameDraft(primaryAlias.display_name || '')
    setDisplayNameOpen(true)
  }

  async function saveDisplayName() {
    if (!primaryAlias) return
    setDisplayNameSaving(true)
    try {
      const res = await api.put('/api/affiliates/me/email/display-name', {
        aliasId: primaryAlias.id,
        displayName: displayNameDraft,
      }, {
        params: apiParams,
      })
      const alias = res.data.alias
      setAliases(prev => prev.map(item => item.id === alias.id ? { ...item, ...alias } : item))
      setDisplayNameDraft(alias.display_name || '')
      setDisplayNameOpen(false)
      toast({ title: 'Sender name saved' })
    } catch (err: any) {
      toast({
        title: 'Sender name failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setDisplayNameSaving(false)
    }
  }

  async function saveSignature() {
    if (!primaryAlias) return
    setSignatureSaving(true)
    try {
      const res = await api.put('/api/affiliates/me/email/signature', {
        aliasId: primaryAlias.id,
        signatureText: signatureDraft.text,
        signatureEnabled: signatureDraft.enabled,
      })
      const alias = res.data.alias
      setAliases(prev => prev.map(item => item.id === alias.id ? { ...item, ...alias } : item))
      setSignatureDraft({
        text: alias.signature_text || '',
        enabled: alias.signature_enabled !== false,
      })
      setSignatureOpen(false)
      toast({ title: 'Signature saved' })
    } catch (err: any) {
      toast({
        title: 'Signature failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSignatureSaving(false)
    }
  }

  // Shared file-picker handler. Reads each File via FileReader, applies
  // client-side validation (count, per-extension blocklist, running total)
  // and appends to the appropriate draft list. Validation errors surface
  // as a single toast — we collect rejections so the user sees "PDF is too
  // big" instead of swallowing it silently next to a successful add.
  async function addAttachmentsTo(
    current: DraftAttachment[],
    files: FileList | File[] | null,
    setDraft: (next: DraftAttachment[]) => void,
  ): Promise<void> {
    if (!files || files.length === 0) return
    const incoming = Array.from(files)
    const next: DraftAttachment[] = [...current]
    const rejections: string[] = []
    let total = current.reduce((sum, a) => sum + a.sizeBytes, 0)
    for (const file of incoming) {
      if (next.length >= ATTACHMENT_MAX_COUNT) {
        rejections.push(`Too many files — limit is ${ATTACHMENT_MAX_COUNT}`)
        break
      }
      const ext = extensionOfFile(file.name)
      if (ext && ATTACHMENT_BLOCKED_EXTENSIONS.has(ext)) {
        rejections.push(`${file.name} — .${ext} blocked for security`)
        continue
      }
      if (file.size <= 0) {
        rejections.push(`${file.name} is empty`)
        continue
      }
      if (total + file.size > ATTACHMENT_MAX_BYTES) {
        rejections.push(`${file.name} — over ${formatBytes(ATTACHMENT_MAX_BYTES)} total cap`)
        continue
      }
      try {
        const base64 = await readFileAsBase64(file)
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          contentBase64: base64,
        })
        total += file.size
      } catch (err: any) {
        rejections.push(`${file.name} — ${err?.message || 'read failed'}`)
      }
    }
    setDraft(next)
    if (rejections.length > 0) {
      toast({
        title: rejections.length === 1 ? 'Attachment rejected' : 'Some attachments rejected',
        description: rejections.join('\n'),
        variant: 'destructive',
      })
    }
  }

  // Downloads go through axios (and its Authorization interceptor) instead
  // of an <a href> — direct clicks on /api URLs don't carry the bearer token
  // we store in localStorage, so they'd just 401. We pull the response as a
  // Blob, wrap it in an object URL, and trigger an off-screen anchor.
  async function downloadAttachment(id: string, filename: string): Promise<void> {
    try {
      const res = await api.get(`/api/affiliates/me/email/attachments/${id}/download`, {
        responseType: 'blob',
        params: apiParams,
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'attachment'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err: any) {
      toast({
        title: 'Download failed',
        description: err?.response?.data?.message || err?.message || 'Could not fetch the file.',
        variant: 'destructive',
      })
    }
  }

  function attachmentPayload(drafts: DraftAttachment[]): Array<{ filename: string; contentType: string; contentBase64: string }> {
    return drafts.map(d => ({
      filename: d.filename,
      contentType: d.contentType,
      contentBase64: d.contentBase64,
    }))
  }

  async function improveDraft(opts: {
    text: string
    subject?: string
    context: 'compose' | 'reply'
    setBusy: (v: boolean) => void
    apply: (next: string) => void
    setUndo: (original: string | null) => void
  }): Promise<void> {
    const draft = opts.text.trim()
    if (!draft) {
      toast({ title: 'Nothing to polish', description: 'Write a few words first.' })
      return
    }
    opts.setBusy(true)
    try {
      const res = await api.post('/api/affiliates/me/email/improve', {
        text: draft,
        subject: opts.subject,
        context: opts.context,
      }, { params: apiParams })
      const improved = (res.data?.improved || '').trim()
      if (!improved) {
        toast({ title: 'No changes suggested', description: 'The draft already reads clean.' })
        return
      }
      if (improved === draft) {
        toast({ title: 'No changes needed', description: 'The draft already reads clean.' })
        return
      }
      opts.setUndo(opts.text) // preserve the exact pre-improve text
      opts.apply(improved)
      toast({ title: 'Polished draft applied', description: 'Tap Undo if you preferred the original.' })
    } catch (err: any) {
      toast({
        title: 'Polish failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      opts.setBusy(false)
    }
  }

  async function sendNewMessage() {
    if (!primaryAlias) return
    setSending(true)
    try {
      const res = await api.post('/api/affiliates/me/email/send', {
        aliasId: primaryAlias.id,
        to: compose.to,
        ...(compose.cc.trim() ? { cc: compose.cc } : {}),
        ...(compose.bcc.trim() ? { bcc: compose.bcc } : {}),
        subject: compose.subject,
        textBody: compose.textBody,
        ...(composeAttachments.length > 0 ? { attachments: attachmentPayload(composeAttachments) } : {}),
      })
      toast({ title: 'Email sent' })
      setCompose({ to: '', cc: '', bcc: '', subject: '', textBody: '' })
      setComposeAttachments([])
      setComposeShowCc(false)
      setComposeShowBcc(false)
      setComposeUndoText(null)
      setComposeOpen(false)
      setFolder('sent')
      await loadThreads('sent')
      if (res.data.thread) {
        await loadThread(res.data.thread)
      }
    } catch (err: any) {
      toast({
        title: 'Email failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSending(false)
    }
  }

  async function sendReply() {
    if (!selectedThread || !replyBody.trim()) return
    setSending(true)
    try {
      await api.post(`/api/affiliates/me/email/threads/${selectedThread.id}/reply`, {
        aliasId: primaryAlias?.id,
        textBody: replyBody,
        ...(replyAttachments.length > 0 ? { attachments: attachmentPayload(replyAttachments) } : {}),
      })
      toast({ title: 'Reply sent' })
      setReplyBody('')
      setReplyAttachments([])
      await loadThread(selectedThread)
      await loadThreads(folder)
    } catch (err: any) {
      toast({
        title: 'Reply failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSending(false)
    }
  }

  // Folder tabs — single visual primitive used in the header instead of the
  // shadcn Tabs default, which leaves a lot of dead space and doesn't accept
  // an unread-count indicator on a single tab cleanly.
  const folderTabs: Array<{ key: Folder; label: string; icon: typeof Inbox; showUnread?: boolean }> = [
    { key: 'inbox', label: 'Inbox', icon: Inbox, showUnread: true },
    { key: 'sent', label: 'Sent', icon: Send },
    { key: 'flagged', label: 'Flagged', icon: Flag },
    { key: 'all', label: 'All', icon: MailOpen },
    { key: 'trash', label: 'Trash', icon: Trash2 },
  ]

  // Embedded compose lives in the right pane when no thread is open and
  // composePopout is false; the same form goes into a dialog when popped
  // out. Both share this JSX so behaviour stays in sync.
  const showEmbeddedCompose = composeOpen && !composePopout
  const composeFormBody = (
    <div className="grid gap-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="affiliate-email-to">To</Label>
          <div className="flex gap-2 text-[11px]">
            {!composeShowCc && (
              <button type="button" onClick={() => setComposeShowCc(true)} className="text-brand-royal hover:underline">
                + Cc
              </button>
            )}
            {!composeShowBcc && (
              <button type="button" onClick={() => setComposeShowBcc(true)} className="text-brand-royal hover:underline">
                + Bcc
              </button>
            )}
          </div>
        </div>
        <Input
          id="affiliate-email-to"
          value={compose.to}
          onChange={event => setCompose(prev => ({ ...prev, to: event.target.value }))}
          placeholder="prospect@example.com, second@example.com"
        />
        <p className="text-[11px] text-muted-foreground">Separate multiple addresses with commas.</p>
      </div>
      {composeShowCc && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="affiliate-email-cc">Cc</Label>
            <button type="button" onClick={() => { setComposeShowCc(false); setCompose(prev => ({ ...prev, cc: '' })) }} className="text-[11px] text-muted-foreground hover:underline">
              Remove
            </button>
          </div>
          <Input
            id="affiliate-email-cc"
            value={compose.cc}
            onChange={event => setCompose(prev => ({ ...prev, cc: event.target.value }))}
            placeholder="cc@example.com"
          />
        </div>
      )}
      {composeShowBcc && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="affiliate-email-bcc">Bcc</Label>
            <button type="button" onClick={() => { setComposeShowBcc(false); setCompose(prev => ({ ...prev, bcc: '' })) }} className="text-[11px] text-muted-foreground hover:underline">
              Remove
            </button>
          </div>
          <Input
            id="affiliate-email-bcc"
            value={compose.bcc}
            onChange={event => setCompose(prev => ({ ...prev, bcc: event.target.value }))}
            placeholder="bcc@example.com"
          />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="affiliate-email-subject">Subject</Label>
        <Input
          id="affiliate-email-subject"
          value={compose.subject}
          onChange={event => setCompose(prev => ({ ...prev, subject: event.target.value }))}
          placeholder="Following up"
        />
      </div>
      <div className="flex flex-1 flex-col min-h-0 space-y-1.5">
        <Label htmlFor="affiliate-email-body" className="sr-only">Message</Label>
        {/* Textarea + flush toolbar — Improve / Undo sit BELOW the field so
            they don't fight with the field they're modifying. */}
        <Textarea
          id="affiliate-email-body"
          value={compose.textBody}
          onChange={event => setCompose(prev => ({ ...prev, textBody: event.target.value }))}
          className={cn(
            'resize-none rounded-b-none border-b-0 text-sm leading-relaxed flex-1',
            showEmbeddedCompose ? 'min-h-[320px]' : 'min-h-[200px]',
          )}
          placeholder="Write your message…"
        />
        <div className="-mt-px flex items-center gap-2 rounded-b-md border border-input bg-muted/30 px-2 py-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-foreground/80 hover:text-foreground"
            onClick={() => improveDraft({
              text: compose.textBody,
              subject: compose.subject,
              context: 'compose',
              setBusy: setComposeImproving,
              apply: next => setCompose(prev => ({ ...prev, textBody: next })),
              setUndo: setComposeUndoText,
            })}
            disabled={composeImproving || sending || isPreview || !compose.textBody.trim()}
            title={isPreview ? 'Preview mode — read only' : 'Polish grammar and clarity with AI'}
          >
            <Sparkles className={cn('mr-1 h-3 w-3 text-brand-royal', composeImproving && 'animate-pulse')} />
            {composeImproving ? 'Polishing…' : 'Improve writing'}
          </Button>
          {composeUndoText !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-[11px] text-muted-foreground"
              onClick={() => {
                setCompose(prev => ({ ...prev, textBody: composeUndoText }))
                setComposeUndoText(null)
              }}
              disabled={sending || composeImproving}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Undo
            </Button>
          )}
          <span className="ml-auto text-[10px] text-muted-foreground">
            Fixes grammar, keeps your voice. No em dashes.
          </span>
        </div>
      </div>
      <AttachmentRow
        attachments={composeAttachments}
        onAdd={files => addAttachmentsTo(composeAttachments, files, setComposeAttachments)}
        onRemove={id => setComposeAttachments(prev => prev.filter(a => a.id !== id))}
        disabled={sending}
      />
    </div>
  )

  const sendButton = (
    <Button
      onClick={sendNewMessage}
      disabled={sending || !compose.to.trim() || !compose.subject.trim() || !compose.textBody.trim()}
      className="bg-brand-royal hover:bg-brand-royal/90"
    >
      <Send className="mr-2 h-4 w-4" />
      {sending ? 'Sending…' : 'Send email'}
    </Button>
  )

  return (
    <Card className="card-inset overflow-hidden">
      {/* ─── Header: slim, one row ───────────────────────────────────
          Restraint pass — the gradient banner, big icon, and duplicate
          title are gone. The breadcrumb on the page already names the
          surface; this row just carries the alias + working controls. */}
      <div className="border-b">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3">
          {primaryAlias && (
            <button
              type="button"
              onClick={copyAlias}
              className="group flex min-w-0 items-center gap-2 text-left transition-colors"
              title="Copy your alias"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span className="min-w-0 truncate text-sm font-medium text-foreground">
                {senderNameLabel}
              </span>
              <span className="hidden shrink-0 text-xs font-mono text-muted-foreground sm:inline">
                {primaryAlias.email_address}
              </span>
              {aliasCopied ? (
                <Check className="h-3 w-3 shrink-0 text-emerald-500" />
              ) : (
                <Copy className="h-3 w-3 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-foreground" />
              )}
            </button>
          )}
          {totalUnread > 0 && (
            <Badge className="h-5 shrink-0 bg-brand-royal px-1.5 text-[10px] text-white hover:bg-brand-royal">
              {totalUnread} unread
            </Badge>
          )}
          {isPreview && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              Read-only preview
            </span>
          )}

          <div className="ml-auto flex shrink-0 items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void load()}
              disabled={loading}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              title="Refresh"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={openDisplayNameEditor}
              disabled={!primaryAlias || isPreview}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              title={isPreview ? 'Preview mode — read only' : 'Edit sender name'}
            >
              <UserRound className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={openSignatureEditor}
              disabled={!primaryAlias || isPreview}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              title={isPreview ? 'Preview mode — read only' : 'Edit signature'}
            >
              <PenLine className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setComposeOpen(true)
                setComposePopout(!!selectedThread)
              }}
              disabled={!primaryAlias || isPreview}
              className="ml-1 h-8 bg-brand-royal hover:bg-brand-royal/90"
              title={isPreview ? 'Preview mode — read only' : undefined}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
              Compose
            </Button>
          </div>
        </div>

        {/* Folder tabs — Linear-style underline. Inactive tabs recede,
            active tab carries a 2px brand-royal underline. New folder is
            a muted text affordance, not a button. */}
        <div className="border-t bg-muted/20">
          <div className="flex items-center gap-6 overflow-x-auto px-5">
            {folderTabs.map(tab => {
              const Icon = tab.icon
              const active = folder === tab.key
              const showCount = tab.showUnread && totalUnread > 0
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setFolder(tab.key)}
                  className={cn(
                    'group relative flex shrink-0 items-center gap-1.5 py-2.5 text-xs font-medium transition-colors',
                    active
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground/85'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={active ? 2.25 : 1.75} />
                  {tab.label}
                  {showCount && (
                    <span className={cn(
                      'inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
                      active ? 'bg-brand-royal text-white' : 'bg-muted text-muted-foreground'
                    )}>
                      {totalUnread}
                    </span>
                  )}
                  <span
                    className={cn(
                      'absolute inset-x-0 -bottom-px h-[2px] rounded-t-sm transition-colors',
                      active ? 'bg-brand-royal' : 'bg-transparent'
                    )}
                  />
                </button>
              )
            })}
            {folders.length > 0 && (
              <span className="h-4 w-px shrink-0 bg-border" aria-hidden />
            )}
            {folders.map(f => {
              const key: Folder = `folder:${f.id}`
              const active = folder === key
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFolder(key)}
                  onContextMenu={event => {
                    event.preventDefault()
                    setFolderMenu({ folderId: f.id, x: event.clientX, y: event.clientY })
                  }}
                  className={cn(
                    'group relative flex max-w-[180px] shrink-0 items-center gap-1.5 py-2.5 text-xs font-medium transition-colors',
                    active
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground/85'
                  )}
                  title={`Right-click to rename or delete · ${f.name}`}
                >
                  <span className="truncate">{f.name}</span>
                  {(f.unread_count || 0) > 0 && (
                    <span className={cn(
                      'inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
                      active ? 'bg-brand-royal text-white' : 'bg-muted text-muted-foreground'
                    )}>
                      {f.unread_count}
                    </span>
                  )}
                  <span
                    className={cn(
                      'absolute inset-x-0 -bottom-px h-[2px] rounded-t-sm transition-colors',
                      active ? 'bg-brand-royal' : 'bg-transparent'
                    )}
                  />
                </button>
              )
            })}
            <button
              type="button"
              onClick={() => { setFolderDraft(''); setNewFolderOpen(true) }}
              disabled={isPreview}
              className="ml-auto flex shrink-0 items-center gap-1 py-2.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              title={isPreview ? 'Preview mode — read only' : 'Create a new folder'}
            >
              <Plus className="h-3 w-3" />
              New folder
            </button>
          </div>
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <CardContent className="p-0">
        {!primaryAlias && !loading ? (
          <div className="mx-5 my-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Email alias setup is pending. Refresh or ask a platform admin to approve the affiliate account again.</span>
          </div>
        ) : (
          <div className="grid min-h-[480px] lg:grid-cols-[minmax(280px,0.85fr)_minmax(0,1.6fr)]">
            {/* Thread list — on mobile, hides once a thread is selected so the
                user lands directly on the message view instead of having to
                scroll past the list to find the message that just opened. */}
            <div
              className={cn(
                'border-r min-h-[480px] flex-col',
                selectedThread ? 'hidden lg:flex' : 'flex'
              )}
            >
              {loading ? (
                <ThreadListSkeleton />
              ) : threads.length === 0 ? (
                <EmptyState
                  folder={folder}
                  hasAlias={!!primaryAlias}
                  onCompose={() => { setComposeOpen(true); setComposePopout(false) }}
                />
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {threads.map(thread => {
                    const isSelected = selectedThread?.id === thread.id
                    const isUnread = thread.unread_count > 0
                    const otherEmail = otherParticipant(thread, primaryAlias?.email_address)
                    const displayName = senderDisplayName(null, otherEmail)
                    return (
                      <button
                        key={thread.id}
                        type="button"
                        onClick={() => {
                          // Auto-popout embedded compose so the thread can
                          // claim the right pane without losing the draft.
                          if (composeOpen && !composePopout) setComposePopout(true)
                          void loadThread(thread)
                        }}
                        onContextMenu={event => {
                          event.preventDefault()
                          setThreadMenu({ threadId: thread.id, x: event.clientX, y: event.clientY })
                        }}
                        className={cn(
                          'group relative block w-full px-4 py-3.5 text-left border-b last:border-b-0',
                          'transition-[background-color,border-color] duration-200 ease-out',
                          'hover:bg-muted/40',
                          isSelected && 'bg-brand-royal/5 dark:bg-brand-royal/10',
                          isUnread && !isSelected && 'bg-background'
                        )}
                      >
                        {/* Active accent bar */}
                        {isSelected && (
                          <span className="absolute left-0 top-0 h-full w-[3px] bg-brand-royal rounded-r" />
                        )}
                        <div className="flex items-start gap-3">
                          <div className={cn('relative shrink-0', !isUnread && 'opacity-80')}>
                            <GradientAvatar
                              seed={otherEmail || thread.id}
                              name={displayName}
                              size={32}
                            />
                            {isUnread && (
                              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-brand-royal ring-2 ring-background" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            {/* Strict hierarchy: sender first, subject second,
                                preview third. Drop the "received"/"sent" pill
                                — direction is implied by sender context. Only
                                surface status when it's actually informative
                                (failed/bounced/etc). */}
                            <div className="flex items-baseline justify-between gap-2">
                              <div className={cn(
                                'truncate text-[13px] leading-tight',
                                isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'
                              )}>
                                {displayName || otherEmail || 'Unknown sender'}
                              </div>
                              <div className="flex shrink-0 items-center gap-1">
                                {thread.muted && (
                                  <BellOff className="h-3 w-3 text-muted-foreground/50" />
                                )}
                                {thread.flagged && (
                                  <Flag className="h-3 w-3 fill-amber-500 text-amber-500" />
                                )}
                                <div className="text-[10px] tabular-nums text-muted-foreground/70">
                                  {formatThreadDate(thread.last_message_at)}
                                </div>
                              </div>
                            </div>
                            <div className={cn(
                              'mt-0.5 truncate text-[12.5px] leading-tight',
                              isUnread ? 'text-foreground/85' : 'text-muted-foreground'
                            )}>
                              {thread.subject || '(no subject)'}
                            </div>
                            <div className="mt-0.5 line-clamp-1 text-[11.5px] leading-snug text-muted-foreground/70">
                              {thread.last_message_preview || ''}
                            </div>
                            {thread.last_status
                              && !['received', 'sent', 'delivered', 'opened', 'clicked'].includes(thread.last_status) && (
                              <div className="mt-1">
                                <Badge
                                  variant={statusVariant(thread.last_status)}
                                  className="h-4 px-1 text-[9px] font-medium uppercase tracking-wide"
                                >
                                  {thread.last_status}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Selected thread view — on mobile, this takes over the full
                pane once a thread is open; the Back button restores the list.
                Embedded compose also takes this pane when active. */}
            <div
              className={cn(
                'flex-col min-h-[480px]',
                selectedThread || showEmbeddedCompose ? 'flex' : 'hidden lg:flex'
              )}
            >
              {showEmbeddedCompose ? (
                <>
                  {/* Slim header — no big icon, no duplicate "Sending from"
                      subtitle. The alias is already in the panel header above. */}
                  <div className="flex items-center justify-between gap-3 border-b px-5 py-2.5">
                    <span className="text-[13px] font-medium text-foreground">New email</span>
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setComposePopout(true)}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Pop out into a separate window"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setComposeOpen(false)}
                        disabled={sending}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Close compose"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-5 py-4">
                    {composeFormBody}
                  </div>
                  {/* Sticky footer — primary action lives where the eye lands
                      after typing. Brand-royal Send is the only filled CTA. */}
                  <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background/95 px-5 py-2.5 backdrop-blur">
                    <Button variant="ghost" size="sm" onClick={() => setComposeOpen(false)} disabled={sending} className="h-8 text-muted-foreground hover:text-foreground">
                      Cancel
                    </Button>
                    {sendButton}
                  </div>
                </>
              ) : !selectedThread ? (
                <NoThreadSelected />
              ) : (
                <>
                  {/* Thread header + action toolbar */}
                  <div className="border-b bg-muted/20 px-5 py-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => { setSelectedThread(null); setMessages([]); setReplyAttachments([]) }}
                        className="lg:hidden inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Back to inbox"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-semibold tracking-tight leading-tight flex items-center gap-2">
                          {selectedThread.flagged && (
                            <Flag className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />
                          )}
                          <span className="truncate">{selectedThread.subject || '(no subject)'}</span>
                        </h3>
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted">
                            <Mail className="h-2.5 w-2.5" />
                          </span>
                          <span className="truncate">
                            {replyRecipients.length > 0
                              ? replyRecipients.join(', ')
                              : 'No external recipients'}
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0 text-[10px] uppercase tracking-wide font-medium">
                        {selectedThread.status}
                      </Badge>
                    </div>
                    {/* Toolbar: Reply · Forward · Flag · Mark read/unread ·
                        Mute · Delete (or Restore in Trash). Same actions
                        available from the right-click menu — duplicated here
                        for one-click reach. */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[12px]"
                        onClick={() => void replyToThread(selectedThread)}
                        disabled={isPreview}
                        title={isPreview ? 'Preview mode — read only' : 'Reply'}
                      >
                        <Reply className="mr-1.5 h-3.5 w-3.5" />
                        Reply
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[12px]"
                        onClick={() => void forwardThread(selectedThread)}
                        disabled={isPreview}
                        title={isPreview ? 'Preview mode — read only' : 'Forward'}
                      >
                        <Forward className="mr-1.5 h-3.5 w-3.5" />
                        Forward
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[12px]"
                        onClick={() => patchThread(
                          selectedThread.id,
                          { flagged: !selectedThread.flagged },
                          { flagged: !selectedThread.flagged },
                        )}
                        disabled={isPreview || actionBusy === selectedThread.id}
                        title={isPreview ? 'Preview mode — read only' : (selectedThread.flagged ? 'Unflag' : 'Flag')}
                      >
                        <Flag className={cn('mr-1.5 h-3.5 w-3.5', selectedThread.flagged && 'text-amber-500 fill-amber-500')} />
                        {selectedThread.flagged ? 'Flagged' : 'Flag'}
                      </Button>
                      {selectedThread.unread_count > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[12px]"
                          onClick={() => patchThread(
                            selectedThread.id,
                            { markRead: true },
                            { unread_count: 0 },
                          )}
                          disabled={isPreview || actionBusy === selectedThread.id}
                          title={isPreview ? 'Preview mode — read only' : 'Mark as read'}
                        >
                          <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
                          Mark read
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[12px]"
                          onClick={() => patchThread(
                            selectedThread.id,
                            { markUnread: true },
                            { unread_count: 1 },
                          )}
                          disabled={isPreview || actionBusy === selectedThread.id}
                          title={isPreview ? 'Preview mode — read only' : 'Mark thread unread'}
                        >
                          <MailPlus className="mr-1.5 h-3.5 w-3.5" />
                          Mark unread
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[12px]"
                        onClick={() => patchThread(
                          selectedThread.id,
                          { muted: !selectedThread.muted },
                          { muted: !selectedThread.muted },
                        )}
                        disabled={isPreview || actionBusy === selectedThread.id}
                        title={isPreview ? 'Preview mode — read only' : (selectedThread.muted ? 'Resume notifications for this thread' : 'Silence notifications for this thread')}
                      >
                        {selectedThread.muted ? <Bell className="mr-1.5 h-3.5 w-3.5" /> : <BellOff className="mr-1.5 h-3.5 w-3.5" />}
                        {selectedThread.muted ? 'Unmute' : 'Mute'}
                      </Button>
                      {selectedThread.deleted_at ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[12px]"
                          onClick={() => patchThread(selectedThread.id, { deleted: false })}
                          disabled={isPreview || actionBusy === selectedThread.id}
                          title={isPreview ? 'Preview mode — read only' : 'Restore to inbox'}
                        >
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                          Restore
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[12px] text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => patchThread(selectedThread.id, { deleted: true })}
                          disabled={isPreview || actionBusy === selectedThread.id}
                          title={isPreview ? 'Preview mode — read only' : 'Move to trash'}
                        >
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                    {threadLoading ? (
                      <ThreadMessagesSkeleton />
                    ) : messages.map(message => {
                      const isOutbound = message.direction === 'outbound'
                      const senderEmail = isOutbound
                        ? message.from_email || primaryAlias?.email_address || ''
                        : message.from_email
                      const senderName = isOutbound
                        ? (message.from_name || primaryAlias?.display_name || 'You')
                        : senderDisplayName(message.from_name, message.from_email)
                      const timestamp = formatFullDate(message.sent_at || message.received_at || message.created_at)
                      return (
                        <div key={message.id} className="flex gap-3">
                          <div className="shrink-0">
                            <GradientAvatar
                              seed={senderEmail || message.id}
                              name={senderName}
                              size={32}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                              <span className="text-sm font-semibold text-foreground leading-tight">
                                {senderName}
                              </span>
                              <span className="text-[11px] text-muted-foreground truncate">
                                {senderEmail}
                              </span>
                              <span className="ml-auto text-[11px] text-muted-foreground/80 tabular-nums">
                                {timestamp}
                              </span>
                            </div>
                            {/* Outlook-style expanded address lines. Only render
                                what's actually populated so empty Cc rows
                                don't add visual noise. */}
                            {message.to_addresses?.length > 0 && (
                              <div className="mt-0.5 text-[11px] text-muted-foreground leading-snug truncate">
                                <span className="text-foreground/60 font-medium">To:</span>{' '}
                                {message.to_addresses.join(', ')}
                              </div>
                            )}
                            {message.cc_addresses?.length > 0 && (
                              <div className="text-[11px] text-muted-foreground leading-snug truncate">
                                <span className="text-foreground/60 font-medium">Cc:</span>{' '}
                                {message.cc_addresses.join(', ')}
                              </div>
                            )}
                            <div className={cn(
                              'mt-1.5 rounded-lg border px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap',
                              isOutbound
                                ? 'border-brand-royal/15 bg-brand-royal/5 dark:border-brand-royal/25 dark:bg-brand-royal/10'
                                : 'border-border bg-background'
                            )}>
                              <MessageBody
                                text={messageText(message)}
                                html={message.html_body}
                                isOutbound={isOutbound}
                                fallback={message.snippet}
                              />
                            </div>
                            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                              {message.status && message.status !== 'received' && (
                                <Badge
                                  variant={statusVariant(message.status)}
                                  className="h-4 px-1.5 text-[9px] uppercase tracking-wide font-medium"
                                >
                                  {message.status}
                                </Badge>
                              )}
                              {message.attachments?.length > 0 && (
                                <>
                                  {message.attachments.map((attachment, index) => {
                                    const label = attachment.filename || 'attachment'
                                    const size = typeof attachment.size === 'number' && attachment.size > 0
                                      ? ` · ${formatBytes(attachment.size)}`
                                      : ''
                                    // Attachments persisted via affiliate_email_attachments carry
                                    // an `id` — those are clickable downloads. Legacy rows (no id)
                                    // fall back to a static badge.
                                    if (attachment.id) {
                                      return (
                                        <button
                                          key={`${message.id}-${attachment.id}`}
                                          type="button"
                                          onClick={() => void downloadAttachment(attachment.id!, label)}
                                          className="inline-flex items-center gap-1 rounded-md border bg-muted/30 px-1.5 py-0.5 text-[10px] font-medium hover:bg-brand-royal/5 hover:border-brand-royal/40 transition-colors"
                                          title={`Download ${label}${size}`}
                                        >
                                          <Paperclip className="h-2.5 w-2.5" />
                                          <span className="max-w-[140px] truncate">{label}</span>
                                          {size && <span className="text-muted-foreground">{size}</span>}
                                        </button>
                                      )
                                    }
                                    return (
                                      <Badge
                                        key={`${message.id}-${index}`}
                                        variant="outline"
                                        className="h-4 px-1.5 text-[9px] font-medium"
                                      >
                                        {label}
                                      </Badge>
                                    )
                                  })}
                                </>
                              )}
                            </div>
                            {message.error_message && (
                              <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span>{message.error_message}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Reply composer */}
                  <div className="border-t bg-muted/10 px-5 py-4">
                    <div className="rounded-lg border bg-background shadow-sm focus-within:border-brand-royal/40 focus-within:ring-2 focus-within:ring-brand-royal/15 transition-[border-color,box-shadow] duration-200 ease-out">
                      <div className="flex items-center gap-2 border-b px-3 py-2 text-[11px] text-muted-foreground">
                        <Sparkles className="h-3 w-3" />
                        <span>
                          Reply as <span className="font-mono text-foreground/80">{primaryAlias?.email_address}</span>
                        </span>
                      </div>
                      <Textarea
                        id="affiliate-email-reply"
                        ref={replyTextareaRef}
                        value={replyBody}
                        onChange={event => setReplyBody(event.target.value)}
                        placeholder="Write your reply…"
                        className="min-h-[100px] border-0 focus-visible:ring-0 resize-none text-sm leading-relaxed"
                      />
                      {replyAttachments.length > 0 && (
                        <div className="border-t px-3 py-2">
                          <AttachmentChips
                            attachments={replyAttachments}
                            onRemove={id => setReplyAttachments(prev => prev.filter(a => a.id !== id))}
                            disabled={sending || isPreview}
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <AttachmentButton
                            disabled={sending || isPreview}
                            onAdd={files => addAttachmentsTo(replyAttachments, files, setReplyAttachments)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 text-[11px]"
                            onClick={() => improveDraft({
                              text: replyBody,
                              subject: selectedThread?.subject,
                              context: 'reply',
                              setBusy: setReplyImproving,
                              apply: setReplyBody,
                              setUndo: setReplyUndoText,
                            })}
                            disabled={replyImproving || sending || isPreview || !replyBody.trim()}
                            title={isPreview ? 'Preview mode — read only' : 'Polish grammar and clarity with AI'}
                          >
                            <Sparkles className={cn('mr-1 h-3.5 w-3.5', replyImproving && 'animate-pulse')} />
                            {replyImproving ? 'Polishing…' : 'Improve'}
                          </Button>
                          {replyUndoText !== null && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 text-[11px] text-muted-foreground"
                              onClick={() => {
                                setReplyBody(replyUndoText)
                                setReplyUndoText(null)
                              }}
                              disabled={sending || replyImproving}
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />
                              Undo
                            </Button>
                          )}
                          <div className="text-[11px] text-muted-foreground">
                            {primaryAlias?.signature_enabled !== false ? 'Signature will be appended.' : 'Signature is off.'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={sendReply}
                          disabled={sending || !replyBody.trim() || replyRecipients.length === 0 || isPreview}
                          className="h-8 bg-brand-royal hover:bg-brand-royal/90"
                          title={isPreview ? 'Preview mode — read only' : undefined}
                        >
                          <Send className="mr-1.5 h-3.5 w-3.5" />
                          {sending ? 'Sending…' : isPreview ? 'Preview only' : 'Send reply'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* ─── Sender name dialog ─────────────────────────────────────── */}
      <Dialog open={displayNameOpen} onOpenChange={setDisplayNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sender name</DialogTitle>
            <DialogDescription>
              This is the name recipients see before {primaryAlias?.email_address || 'your affiliate email address'}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="affiliate-email-display-name">Display name</Label>
              <Input
                id="affiliate-email-display-name"
                value={displayNameDraft}
                onChange={event => setDisplayNameDraft(event.target.value)}
                maxLength={120}
                placeholder="Legal Lady"
              />
              <div className="text-xs text-muted-foreground">
                Leave blank to use the affiliate name or Legal Lady.
              </div>
            </div>
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              <div className="text-xs text-muted-foreground">Preview</div>
              <div className="mt-1 truncate font-medium">
                {(displayNameDraft.trim() || senderNameLabel)} &lt;{primaryAlias?.email_address || 'you@partners.legallady.ai'}&gt;
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisplayNameOpen(false)} disabled={displayNameSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveDisplayName}
              disabled={displayNameSaving || !primaryAlias || isPreview}
              className="bg-brand-royal hover:bg-brand-royal/90"
            >
              {displayNameSaving ? 'Saving…' : 'Save sender name'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Signature dialog ────────────────────────────────────────── */}
      <Dialog open={signatureOpen} onOpenChange={setSignatureOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email signature</DialogTitle>
            <DialogDescription>
              Saved for {primaryAlias?.email_address || 'your affiliate alias'}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 px-3.5 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="affiliate-email-signature-enabled" className="text-sm font-medium">
                  Add signature automatically
                </Label>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Applies to new messages and replies.
                </div>
              </div>
              <Switch
                id="affiliate-email-signature-enabled"
                checked={signatureDraft.enabled}
                onCheckedChange={checked => setSignatureDraft(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="affiliate-email-signature-text">Signature text</Label>
              <Textarea
                id="affiliate-email-signature-text"
                value={signatureDraft.text}
                onChange={event => setSignatureDraft(prev => ({ ...prev, text: event.target.value }))}
                className="min-h-[140px] font-mono text-xs"
                maxLength={4000}
                placeholder={'Your name\nLegalLady.AI Partner\nyou@partners.legallady.ai'}
              />
            </div>
            <div className="rounded-lg border bg-background p-4">
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Preview</div>
              <div className={cn('border-l-2 border-brand-royal pl-3', !signatureDraft.enabled && 'opacity-50')}>
                {signaturePreviewLines.length > 0 ? (
                  <>
                    <div className="text-sm font-semibold text-foreground">{signaturePreviewLines[0]}</div>
                    <div className="mt-1 space-y-0.5">
                      {signaturePreviewLines.slice(1).map((line, index) => (
                        <div key={`${line}-${index}`} className="text-xs text-muted-foreground">
                          {line}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs font-semibold text-brand-royal">LegalLady.AI</div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">No signature text.</div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureOpen(false)} disabled={signatureSaving}>
              Cancel
            </Button>
            <Button onClick={saveSignature} disabled={signatureSaving || !primaryAlias} className="bg-brand-royal hover:bg-brand-royal/90">
              {signatureSaving ? 'Saving…' : 'Save signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Compose dialog (only when popped out) ─────────────────────
          Shares composeFormBody with the embedded view so behaviour stays
          in sync regardless of mode. */}
      <Dialog
        open={composeOpen && composePopout}
        onOpenChange={open => { if (!open) setComposeOpen(false) }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-6">
              <span>New email</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setComposePopout(false)}
                className="h-7 text-[11px]"
                title="Embed in the right pane"
              >
                <Minimize2 className="mr-1.5 h-3 w-3" />
                Pop in
              </Button>
            </DialogTitle>
            <DialogDescription>
              Sending from <span className="font-mono">{senderIdentity}</span>
              {primaryAlias?.signature_enabled !== false ? ' with your saved signature.' : '.'}
            </DialogDescription>
          </DialogHeader>
          {composeFormBody}
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={sending}>Cancel</Button>
            {sendButton}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Right-click context menu — appears at cursor for any thread row.
          Uses native onContextMenu + a fixed-position div instead of pulling
          in a radix context-menu primitive that isn't installed. */}
      {threadMenu && (() => {
        const target = threads.find(t => t.id === threadMenu.threadId)
        if (!target) return null
        const close = () => setThreadMenu(null)
        return (
          <ThreadContextMenu
            x={threadMenu.x}
            y={threadMenu.y}
            onClose={close}
          >
            {/* Reply actions sit at the top — they're what you most likely
                want when you right-click an email. */}
            <ThreadContextMenuItem
              icon={Reply}
              label="Reply"
              disabled={isPreview}
              onSelect={() => { close(); void replyToThread(target) }}
            />
            <ThreadContextMenuItem
              icon={ReplyAll}
              label="Reply all"
              disabled={isPreview}
              onSelect={() => { close(); void replyToThread(target) }}
            />
            <ThreadContextMenuItem
              icon={Forward}
              label="Forward"
              disabled={isPreview}
              onSelect={() => { close(); void forwardThread(target) }}
            />
            <div className="my-1 border-t border-border" />
            <ThreadContextMenuItem
              icon={Flag}
              label={target.flagged ? 'Unflag' : 'Flag'}
              disabled={isPreview}
              onSelect={() => {
                close()
                void patchThread(target.id, { flagged: !target.flagged }, { flagged: !target.flagged })
              }}
            />
            {target.unread_count > 0 ? (
              <ThreadContextMenuItem
                icon={CheckCheck}
                label="Mark as read"
                disabled={isPreview}
                onSelect={() => {
                  close()
                  void patchThread(target.id, { markRead: true }, { unread_count: 0 })
                }}
              />
            ) : (
              <ThreadContextMenuItem
                icon={MailPlus}
                label="Mark unread"
                disabled={isPreview}
                onSelect={() => {
                  close()
                  void patchThread(target.id, { markUnread: true }, { unread_count: 1 })
                }}
              />
            )}
            <ThreadContextMenuItem
              icon={target.muted ? Bell : BellOff}
              label={target.muted ? 'Unmute notifications' : 'Mute notifications'}
              disabled={isPreview}
              onSelect={() => {
                close()
                void patchThread(target.id, { muted: !target.muted }, { muted: !target.muted })
              }}
            />
            {folders.length > 0 && (
              <div className="my-1 border-t border-border" />
            )}
            {folders.map(f => (
              <ThreadContextMenuItem
                key={f.id}
                icon={MoveRight}
                label={`Move to ${f.name}`}
                disabled={isPreview || target.folder_id === f.id}
                onSelect={() => {
                  close()
                  void patchThread(target.id, { folderId: f.id })
                }}
              />
            ))}
            {target.folder_id && (
              <ThreadContextMenuItem
                icon={MoveLeft}
                label="Remove from folder"
                disabled={isPreview}
                onSelect={() => { close(); void patchThread(target.id, { folderId: null }) }}
              />
            )}
            <div className="my-1 border-t border-border" />
            {target.deleted_at ? (
              <ThreadContextMenuItem
                icon={RotateCcw}
                label="Restore"
                disabled={isPreview}
                onSelect={() => { close(); void patchThread(target.id, { deleted: false }) }}
              />
            ) : (
              <ThreadContextMenuItem
                icon={Trash2}
                label="Delete"
                destructive
                disabled={isPreview}
                onSelect={() => { close(); void patchThread(target.id, { deleted: true }) }}
              />
            )}
          </ThreadContextMenu>
        )
      })()}

      {/* Folder tab right-click menu — Rename / Delete */}
      {folderMenu && (() => {
        const target = folders.find(f => f.id === folderMenu.folderId)
        if (!target) return null
        const close = () => setFolderMenu(null)
        return (
          <ThreadContextMenu x={folderMenu.x} y={folderMenu.y} onClose={close}>
            <ThreadContextMenuItem
              icon={PenLine}
              label="Rename"
              disabled={isPreview}
              onSelect={() => { close(); setFolderDraft(target.name); setRenameTarget(target) }}
            />
            <ThreadContextMenuItem
              icon={Trash2}
              label="Delete"
              destructive
              disabled={isPreview}
              onSelect={() => { close(); void deleteFolder(target) }}
            />
          </ThreadContextMenu>
        )
      })()}

      {/* Create / rename folder dialog (single dialog reused for both) */}
      <Dialog
        open={newFolderOpen || !!renameTarget}
        onOpenChange={(open) => { if (!open) { setNewFolderOpen(false); setRenameTarget(null); setFolderDraft('') } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{renameTarget ? `Rename "${renameTarget.name}"` : 'New folder'}</DialogTitle>
            <DialogDescription>
              {renameTarget
                ? 'Update the folder name. Threads inside stay where they are.'
                : 'Group related threads (e.g. "Prospects", "Closed Deals"). Right-click any folder tab to rename or delete later.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-folder-name">Folder name</Label>
            <Input
              id="affiliate-folder-name"
              value={folderDraft}
              onChange={event => setFolderDraft(event.target.value)}
              maxLength={80}
              placeholder="Prospects"
              autoFocus
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  if (renameTarget) void renameFolder()
                  else void createFolder()
                }
              }}
            />
            <p className="text-[11px] text-muted-foreground">Up to 80 characters. Names must be unique across your folders.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setNewFolderOpen(false); setRenameTarget(null); setFolderDraft('') }}
              disabled={folderSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={renameTarget ? renameFolder : createFolder}
              disabled={folderSaving || !folderDraft.trim()}
              className="bg-brand-royal hover:bg-brand-royal/90"
            >
              {folderSaving ? 'Saving…' : (renameTarget ? 'Rename folder' : 'Create folder')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function ThreadListSkeleton() {
  return (
    <div className="space-y-0 divide-y">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="flex items-start gap-3 px-4 py-3.5">
          <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
            <div className="h-2.5 w-3/4 rounded bg-muted/70 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/50 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

function ThreadMessagesSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1].map(i => (
        <div key={i} className="flex gap-3">
          <div className="h-8 w-8 rounded-full bg-muted animate-pulse shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
            <div className="h-16 rounded-lg bg-muted/60 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState({
  folder,
  hasAlias,
  onCompose,
}: {
  folder: Folder
  hasAlias: boolean
  onCompose: () => void
}) {
  const systemConfig: Partial<Record<Folder, { icon: typeof Inbox; title: string; body: string }>> = {
    inbox: {
      icon: Inbox,
      title: 'Your inbox is quiet',
      body: 'Replies to your emails will land here as soon as they arrive.',
    },
    sent: {
      icon: Send,
      title: 'Nothing sent yet',
      body: 'Compose your first email to start tracking replies and engagement here.',
    },
    flagged: {
      icon: Flag,
      title: 'Nothing flagged',
      body: 'Flag a thread (right-click or use the toolbar) to keep it close by.',
    },
    all: {
      icon: MailOpen,
      title: 'No threads yet',
      body: 'All conversations — sent, received, and in-progress — show up here.',
    },
    trash: {
      icon: Trash2,
      title: 'Trash is empty',
      body: 'Deleted threads land here until you remove them for good.',
    },
  }
  // User-defined folders fall through to a generic empty state. The folder
  // tab strings them as `folder:<uuid>`, so the configMap lookup misses them
  // and crashed the previous render.
  const config = systemConfig[folder] || {
    icon: Inbox,
    title: 'This folder is empty',
    body: 'Right-click a thread elsewhere in your inbox and pick "Move to..." to file it here.',
  }
  const Icon = config.icon
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
        <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-sm font-medium text-foreground">{config.title}</div>
        <div className="mt-1 max-w-xs text-xs text-muted-foreground leading-relaxed">{config.body}</div>
      </div>
      {hasAlias && folder !== 'inbox' && (
        <Button size="sm" variant="outline" onClick={onCompose} className="mt-1">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Compose
        </Button>
      )}
    </div>
  )
}

function NoThreadSelected() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-royal/15 to-brand-periwinkle/5 border border-brand-royal/15">
        <Mail className="h-5 w-5 text-brand-royal/70" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-sm font-medium text-foreground">Pick a thread</div>
        <div className="mt-1 max-w-xs text-xs text-muted-foreground leading-relaxed">
          Select an email from the list to read, reply, or follow up.
        </div>
      </div>
    </div>
  )
}

// Stacked file picker — used by the Compose dialog where vertical space is OK.
function AttachmentRow({
  attachments,
  onAdd,
  onRemove,
  disabled,
}: {
  attachments: DraftAttachment[]
  onAdd: (files: FileList | File[] | null) => void
  onRemove: (id: string) => void
  disabled?: boolean
}) {
  const total = attachments.reduce((sum, a) => sum + a.sizeBytes, 0)
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Attachments</Label>
        <div className="text-[11px] text-muted-foreground">
          {attachments.length}/{ATTACHMENT_MAX_COUNT} files · {formatBytes(total)}/{formatBytes(ATTACHMENT_MAX_BYTES)}
        </div>
      </div>
      <AttachmentButton onAdd={onAdd} disabled={disabled} label="Add files" />
      {attachments.length > 0 && (
        <AttachmentChips attachments={attachments} onRemove={onRemove} disabled={disabled} />
      )}
    </div>
  )
}

// Compact picker for the reply composer — same handler, just a paperclip
// button. No file list shown here; chips live in a sibling band above.
function AttachmentButton({
  onAdd,
  disabled,
  label,
}: {
  onAdd: (files: FileList | File[] | null) => void
  disabled?: boolean
  label?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={event => {
          onAdd(event.target.files)
          event.target.value = '' // allow re-picking the same file
        }}
        disabled={disabled}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="h-8"
      >
        <Paperclip className="mr-1.5 h-3.5 w-3.5" />
        {label || 'Attach'}
      </Button>
    </>
  )
}

function AttachmentChips({
  attachments,
  onRemove,
  disabled,
}: {
  attachments: DraftAttachment[]
  onRemove: (id: string) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {attachments.map(a => (
        <div
          key={a.id}
          className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-[11px]"
          title={`${a.filename} (${formatBytes(a.sizeBytes)})`}
        >
          <Paperclip className="h-3 w-3 text-muted-foreground" />
          <span className="max-w-[160px] truncate">{a.filename}</span>
          <span className="text-muted-foreground">{formatBytes(a.sizeBytes)}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(a.id)}
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              aria-label={`Remove ${a.filename}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

// Lightweight right-click menu. Lives in a Portal-free fixed-position div
// because radix-context-menu isn't installed here; this saves the dep add
// while still giving us click-outside + Escape dismissal. Position is
// clamped to the viewport so the menu doesn't render off-screen when the
// row is near the edge.
function ThreadContextMenu({
  x,
  y,
  onClose,
  children,
}: {
  x: number
  y: number
  onClose: () => void
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x, y })

  useEffect(() => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const maxX = window.innerWidth - rect.width - 8
    const maxY = window.innerHeight - rect.height - 8
    setPosition({
      x: Math.min(x, maxX),
      y: Math.min(y, maxY),
    })
  }, [x, y])

  useEffect(() => {
    const handle = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onClose, true)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onClose, true)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{ top: position.y, left: position.x }}
      className="fixed z-50 min-w-[160px] rounded-md border bg-popover text-popover-foreground shadow-md py-1"
      onContextMenu={event => event.preventDefault()}
    >
      {children}
    </div>
  )
}

function ThreadContextMenuItem({
  icon: Icon,
  label,
  onSelect,
  disabled,
  destructive,
}: {
  icon: typeof Flag
  label: string
  onSelect: () => void
  disabled?: boolean
  destructive?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left',
        'hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed',
        destructive && 'text-destructive hover:bg-destructive/10',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

// Renders an email body with the prior-thread quote chain (the
// "On <date>, <name> wrote: > ..." cruft) tucked behind a "Show quoted
// text" toggle, Gmail-style. Each message gets its own toggle state so
// expanding one doesn't fan out across the thread.
// Render inbound HTML in a sandboxed iframe. Email signatures rely on inline
// styles, table layouts, and rules that fight with the dashboard's own CSS
// (and vice versa) — Gmail solves the same problem with an iframe per
// message. Sandbox flags: no allow-scripts so any leftover JS is inert; no
// allow-same-origin so the iframe document can't reach our cookies. Height
// is measured via a load handler — without same-origin we read it from the
// iframe's intrinsic scroll height once content settles.
function InboundHtmlFrame({ html }: { html: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState<number>(80)

  // Build the iframe document. Minimal base styles keep the body padded and
  // readable; everything else comes from the sender's own CSS. We also wire
  // a tiny script-less hack: set the body's `data-h` attribute via a CSS
  // trick is impossible without scripts, so instead we let allow-same-origin
  // back in but guard against scripts via sandbox below. NOTE: srcDoc gives
  // a null-origin document by default, but the parent can still measure
  // bounding rect via offsetHeight on contentDocument when allow-same-origin
  // is granted. Scripts stay disabled because we omit allow-scripts.
  const doc = useMemo(() => `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html, body { margin: 0; padding: 0; }
    body {
      font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      font-size: 14px;
      line-height: 1.55;
      color: #0d0a1f;
      background: #ffffff;
      padding: 14px 16px;
      word-wrap: break-word;
    }
    img { max-width: 100%; height: auto; border: 0; }
    a { color: #4d71fd; }
    table { border-collapse: separate; }
    p { margin: 0 0 12px 0; }
  </style></head><body>${html}</body></html>`, [html])

  // After load, measure the content height so the iframe doesn't ship a
  // scrollbar. Re-run on `doc` changes (different message body).
  useEffect(() => {
    const node = frameRef.current
    if (!node) return
    const measure = () => {
      try {
        const inner = node.contentDocument
        if (!inner?.body) return
        const measured = Math.max(
          inner.body.scrollHeight,
          inner.documentElement.scrollHeight,
          60,
        )
        setHeight(measured + 16)
      } catch {
        // Cross-origin (shouldn't happen with allow-same-origin) — leave height alone.
      }
    }
    node.addEventListener('load', measure)
    const interval = window.setInterval(measure, 750) // catch late-loaded images
    return () => {
      node.removeEventListener('load', measure)
      window.clearInterval(interval)
    }
  }, [doc])

  return (
    <iframe
      ref={frameRef}
      srcDoc={doc}
      sandbox="allow-same-origin allow-popups"
      title="Email content"
      style={{
        width: '100%',
        height: `${height}px`,
        border: 'none',
        display: 'block',
        backgroundColor: '#ffffff',
        borderRadius: '6px',
      }}
    />
  )
}

function MessageBody({
  text,
  html,
  isOutbound,
  fallback,
}: {
  text: string
  html?: string | null
  isOutbound?: boolean
  fallback?: string | null
}) {
  const [showQuoted, setShowQuoted] = useState(false)

  // Inbound HTML path: render the sender's actual HTML (sanitized) so logos
  // and rich signatures come through. We use the text body for quote-split
  // detection because it's reliable across clients, then collapse the
  // matching quote section in the HTML by string-replace. If the sanitized
  // HTML doesn't contain the boundary text exactly (different whitespace,
  // wrapped in markup), we fall through and render the whole HTML — better
  // than corrupting it.
  const sanitizedHtml = useMemo(
    () => (html && !isOutbound ? sanitizeInboundHtml(html) : ''),
    [html, isOutbound],
  )

  if (!text && !sanitizedHtml) {
    return fallback
      ? <>{fallback}</>
      : <span className="text-muted-foreground italic">No message body.</span>
  }

  // Outbound or plain-text path — same as before, just paragraphs of text.
  if (!sanitizedHtml) {
    const { fresh, quoted } = splitQuotedContent(text)
    return (
      <>
        {fresh || (
          <span className="text-muted-foreground italic">No message body.</span>
        )}
        {quoted && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowQuoted(v => !v)}
              className="inline-flex items-center gap-1 rounded border bg-muted/30 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-brand-royal/5 hover:border-brand-royal/40 hover:text-foreground transition-colors"
            >
              <span className="text-base leading-none">…</span>
              <span>{showQuoted ? 'Hide quoted text' : 'Show quoted text'}</span>
            </button>
            {showQuoted && (
              <div className="mt-2 border-l-2 border-muted-foreground/20 pl-3 text-[13px] text-muted-foreground/85 whitespace-pre-wrap">
                {quoted}
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  // Inbound HTML path. Quoted-text collapse here is best-effort: we still
  // render the full sanitized HTML, and if a quoted text section was
  // detected we render it visually-subdued in a separate block — Gmail
  // does something similar when it can't reach into nested HTML cleanly.
  const { quoted: quotedText } = splitQuotedContent(text)
  return (
    <>
      <InboundHtmlFrame html={sanitizedHtml} />
      {quotedText && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowQuoted(v => !v)}
            className="inline-flex items-center gap-1 rounded border bg-muted/30 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-brand-royal/5 hover:border-brand-royal/40 hover:text-foreground transition-colors"
          >
            <span className="text-base leading-none">…</span>
            <span>{showQuoted ? 'Hide previous thread' : 'Show previous thread'}</span>
          </button>
          {showQuoted && (
            <div className="mt-2 border-l-2 border-muted-foreground/20 pl-3 text-[13px] text-muted-foreground/85 whitespace-pre-wrap">
              {quotedText}
            </div>
          )}
        </div>
      )}
    </>
  )
}
